import { Step } from '@/components/navigation/navigation-stepper';
import { EncounterResponse, EncounterStatusIdsType, WorkflowResponse } from '@/api/api.schemas';
import { TranslationKey } from '../translations';
import compact from 'lodash/compact';
import { CONDITION_PAGE_STATUS_INCREMENT } from '@/util/constants';

interface LeafVisitStep {
  name: TranslationKey;
  path: string;
  statusId: EncounterStatusIdsType;
  subpaths?: string[] | RegExp;
}

interface PreCreateVisitStep extends LeafVisitStep {
  nextPath?: string;
}

interface ParentVisitStep {
  name: TranslationKey;
  children: LeafStepsWithoutCondition[];
}

// Condition steps are dynamically configured and their names come from the database
// and are therefor ready-made strings that should not be translated
interface ConditionLeafVisitStep extends Omit<LeafVisitStep, 'name'> {
  name: string;
}

interface ConditionParentVisitStep {
  name: string;
  children: ConditionLeafVisitStep[];
}

type AnyLeafStep = LeafVisitStep | ConditionLeafVisitStep | PreCreateVisitStep;

type LeafStepsWithoutCondition = LeafVisitStep | PreCreateVisitStep;

type VisitStepNode = LeafVisitStep | PreCreateVisitStep | ParentVisitStep;

function isLeafVisitStep(step: VisitStepNode | ConditionLeafVisitStep): step is LeafVisitStep {
  return 'statusId' in step;
}

function isPreCreateVisitStep(
  step: VisitStepNode | ConditionLeafVisitStep,
): step is PreCreateVisitStep {
  return 'nextPath' in step;
}

function isParentVisitStep(
  step: VisitStepNode | ConditionParentVisitStep,
): step is ParentVisitStep {
  return 'children' in step;
}

const policiesConsentLeafStep: PreCreateVisitStep = {
  name: 'navigators.visit.policiesConsentStep',
  path: '/visit/policies-consent',
  nextPath: '/visit/pre-screen-questions',
  statusId: EncounterStatusIdsType.Consent,
};

const preScreenQuestionsStep: PreCreateVisitStep = {
  name: 'navigators.visit.preScreenQuestionsStep',
  path: '/visit/pre-screen-questions',
  nextPath: '/visit/:encounterId/questions',
  statusId: EncounterStatusIdsType.Consent,
};

const insuranceInformationStep: LeafVisitStep = {
  name: 'navigators.visit.insuranceInformationStep',
  path: '/visit/:encounterId/insurance-information',
  subpaths: ['/visit/:encounterId/insurance-coverage'],
  statusId: EncounterStatusIdsType.InsuranceInformation,
};

const providerSelectionStep: LeafVisitStep = {
  name: 'navigators.visit.providerSelectionStep',
  path: '/visit/:encounterId/choose-provider',
  statusId: EncounterStatusIdsType.ProviderSelection,
};

const pharmacySelectionStep: LeafVisitStep = {
  name: 'navigators.visit.pharmacySelectionStep',
  path: '/visit/:encounterId/choose-pharmacy',
  subpaths: ['/visit/:encounterId/pharmacy-type-selection'],
  statusId: EncounterStatusIdsType.PharmacySelection,
};

const generalMedicalHistoryStep: LeafVisitStep = {
  name: 'navigators.visit.medicalHistoryStep',
  path: '/visit/:encounterId/medical-history',
  statusId: EncounterStatusIdsType.ClinicalInformationMedicalHistory,
};

const medicalIntakeStep: LeafVisitStep = {
  name: 'navigators.visit.healthAndLifestyleStep',
  path: '/visit/:encounterId/health-and-lifestyle',
  statusId: EncounterStatusIdsType.ClinicalInformationMedicalIntake,
};

const reviewDetailsStep: LeafVisitStep = {
  name: 'navigators.visit.reviewDetailsStep',
  path: '/visit/:encounterId/review',
  statusId: EncounterStatusIdsType.PreSubmitReview,
};

const identityVerificationStep: LeafVisitStep = {
  name: 'navigators.visit.identityVerificationStep',
  path: '/visit/:encounterId/identity-verification',
  subpaths: [
    '/visit/:encounterId/identity-verification/dependent',
    '/visit/:encounterId/identity-verification/rejection',
    '/visit/:encounterId/identity-verification/manual-request',
  ],
  statusId: EncounterStatusIdsType.PreSubmitIdentityVerification,
};

const paymentAndInsuranceStep: LeafVisitStep = {
  name: 'navigators.visit.paymentStep',
  path: '/visit/:encounterId/payment',
  statusId: EncounterStatusIdsType.PreSubmitPaymentInfo,
};

const policiesConsentStep: ParentVisitStep = {
  name: 'navigators.visit.policiesConsentStep',
  children: [policiesConsentLeafStep, preScreenQuestionsStep, insuranceInformationStep],
};

const visitDetailsStep: ParentVisitStep = {
  name: 'navigators.visit.visitDetailsStep',
  children: [providerSelectionStep, pharmacySelectionStep],
};

const clinicalHistoryStep: ParentVisitStep = {
  name: 'navigators.visit.clinicalHistoryStep',
  children: [medicalIntakeStep, generalMedicalHistoryStep],
};

const reviewAndPayStep: ParentVisitStep = {
  name: 'navigators.visit.reviewAndPayStep',
  children: [reviewDetailsStep, identityVerificationStep, paymentAndInsuranceStep],
};

const VISIT_STEPS: (VisitStepNode | 'condition-step')[] = [
  policiesConsentStep,
  'condition-step',
  visitDetailsStep,
  clinicalHistoryStep,
  reviewAndPayStep,
];

function constructStepPath(path: string, encounterId?: number): string {
  return encounterId ? path.replace(':encounterId', encounterId.toString()) : path;
}

function normalizePath(path: string): string {
  return path === '/' ? path : path.replace(/\/$/, '');
}

function isLeafActive(step: AnyLeafStep, pathname: string, encounterId?: number): boolean {
  const path = normalizePath(constructStepPath(step.path, encounterId));
  const normalizedPathname = normalizePath(pathname);

  const isSubpathActive =
    step.subpaths instanceof RegExp
      ? new RegExp(constructStepPath(step.subpaths.source, encounterId)).test(normalizedPathname)
      : (step.subpaths
          ?.map((subpath) => normalizePath(constructStepPath(subpath, encounterId)))
          .includes(normalizedPathname) ?? false);

  return normalizedPathname === path || isSubpathActive;
}

function isLeafCompleted(
  step: AnyLeafStep,
  hasCompletedStep: (statusId: EncounterStatusIdsType) => boolean,
  pathname: string,
  encounterStatusId?: number,
): boolean {
  if (!encounterStatusId) {
    // For pre-create steps (where no encounter exists yet),
    // we cannot rely on backend status to determine completion.
    // Instead, we infer that a step is completed if the user
    // has successfully navigated to the *next* path in the flow.
    if (isPreCreateVisitStep(step)) {
      return step.nextPath === pathname;
    }

    return false;
  }

  if (!isLeafVisitStep(step)) {
    return true;
  }

  return hasCompletedStep(step.statusId);
}

type FormatParameters = {
  pathname: string;
  encounter: EncounterResponse | undefined;
  hasCompletedStep: (statusId: EncounterStatusIdsType) => boolean;
  t: (key: TranslationKey) => string;
};

function formatGenericLeafStep(
  step: AnyLeafStep,
  formatParameters: Omit<FormatParameters, 't'>,
): Omit<Step, 'name'> {
  const { pathname, encounter, hasCompletedStep } = formatParameters;
  const { id: encounterId, statusId: encounterStatusId } = encounter ?? {};

  // Determine if we're in the post-create flow by checking if the pathname contains an encounterId.
  // Pre-create steps have paths like '/visit/policies-consent' or '/visit/pre-screen-questions'
  // (no encounterId).
  // We use pathname instead of encounter existence because:
  // 1. It's more reliable during loading states (encounter may be undefined while loading)
  // 2. It prevents pre-create steps from being navigable once we've moved past them in the flow
  const isInPostCreateFlow = /\/visit\/\d+\//.test(pathname);

  return {
    path: constructStepPath(step.path, encounterId),
    isActive: isLeafActive(step, pathname, encounterId),
    isCompleted: isLeafCompleted(step, hasCompletedStep, pathname, encounterStatusId),
    statusId: isLeafVisitStep(step) ? step.statusId : undefined,
    nonNavigable:
      !isLeafVisitStep(step) ||
      (isPreCreateVisitStep(step) && isInPostCreateFlow) ||
      (step.statusId === EncounterStatusIdsType.PreSubmitIdentityVerification &&
        hasCompletedStep(EncounterStatusIdsType.PreSubmitIdentityVerification)),
  };
}

function formatLeafStep(
  step: LeafVisitStep | PreCreateVisitStep,
  formatParameters: FormatParameters,
): Step {
  return {
    name: formatParameters.t(step.name),
    ...formatGenericLeafStep(step, formatParameters),
  };
}

function formatGenericParentStep(formattedChildren: Step[]): Omit<Step, 'name'> {
  return {
    nonNavigable: formattedChildren.every((child) => child.nonNavigable),
    // We can set the path to '' because it will be nonNavigable so it does not matter
    // what this step navigates with its path
    path: formattedChildren.filter((child) => !child.nonNavigable)[0]?.path ?? '',
    isActive: formattedChildren.some((child) => child.isActive),
    isCompleted: formattedChildren.every((child) => child.isCompleted),
    children: formattedChildren,
  };
}

function formatParentStep(step: ParentVisitStep, formatParameters: FormatParameters): Step {
  const formattedChildren = step.children.map((child) => formatLeafStep(child, formatParameters));

  return {
    name: formatParameters.t(step.name),
    ...formatGenericParentStep(formattedChildren),
  };
}

function formatStep(step: VisitStepNode, formatParameters: FormatParameters): Step {
  if (isParentVisitStep(step)) {
    return formatParentStep(step, formatParameters);
  }
  return formatLeafStep(step, formatParameters);
}

function formatConditionLeafStep(
  step: ConditionLeafVisitStep,
  formatParameters: Omit<FormatParameters, 't'>,
): Step {
  return {
    name: step.name,
    ...formatGenericLeafStep(step, formatParameters),
  };
}

function formatConditionStep(
  step: ConditionParentVisitStep,
  formatParameters: Omit<FormatParameters, 't'>,
): Step {
  const formattedChildren = step.children.map((child) =>
    formatConditionLeafStep(child, formatParameters),
  );

  return {
    name: step.name,
    ...formatGenericParentStep(formattedChildren),
  };
}

function getConditionPageStatusId(pageIndex: number): EncounterStatusIdsType {
  return (EncounterStatusIdsType.ConditionInformationPage1 +
    pageIndex * CONDITION_PAGE_STATUS_INCREMENT) as EncounterStatusIdsType;
}

function createConditionStep(workflow: WorkflowResponse | undefined) {
  if (!workflow || workflow.pages.length === 0) {
    return undefined;
  }

  return {
    name: workflow.label,
    children: workflow.pages.map((p, index) => ({
      name: p.label,
      path: `/visit/:encounterId/questions/${p.pageId}`,
      statusId: getConditionPageStatusId(index),
      subpaths: index === 0 ? ['/visit/:encounterId/questions/'] : undefined,
    })),
  };
}

interface FormatVisitStepsParameters extends FormatParameters {
  workflow: WorkflowResponse | undefined;
  hasInsurance: boolean;
  hasIdVerification: boolean;
}

export function formatVisitSteps(parameters: FormatVisitStepsParameters) {
  const { workflow, hasInsurance, hasIdVerification, ...formatParams } = parameters;
  const conditionStep = createConditionStep(workflow);

  return compact(
    VISIT_STEPS.map<Step | undefined>((step) => {
      if (step === 'condition-step') {
        if (conditionStep) {
          return formatConditionStep(conditionStep, formatParams);
        }

        // This should only be reached when a visit has not yet been created,
        // so we assume the next step will be called "Condition"
        return {
          isActive: false,
          isCompleted: false,
          name: 'Condition',
          path: '/',
        };
      }

      if (step.name === policiesConsentLeafStep.name && isParentVisitStep(step)) {
        const filteredChildren = hasInsurance
          ? step.children
          : step.children.filter((child) => child.statusId !== insuranceInformationStep.statusId);

        const newName: TranslationKey = hasInsurance
          ? 'navigators.visit.policiesConsentWithInsuranceStep'
          : step.name;

        const stepWithFilteredChildren = { ...step, name: newName, children: filteredChildren };
        return formatParentStep(stepWithFilteredChildren, formatParams);
      }

      if (step.name === reviewAndPayStep.name && isParentVisitStep(step)) {
        const filteredChildren = hasIdVerification
          ? step.children
          : step.children.filter((child) => child.statusId !== identityVerificationStep.statusId);

        const stepWithFilteredChildren = { ...step, children: filteredChildren };
        return formatParentStep(stepWithFilteredChildren, formatParams);
      }

      return formatStep(step, formatParams);
    }),
  );
}
